import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Modal } from '@/components/Modal';
import { LoadingBlock } from '@/components/PageHeader';
import {
  RELEASE_NOTES_BY_DATE,
  RELEASE_NOTE_ROWS,
  noteKey,
  type ReleaseArea,
  type ReleaseItem,
} from '@/lib/releaseNotes';
import { groupNotesByBuild } from '@/lib/releaseNoteGroups';
import {
  useAppVersion,
  useBackendBuild,
  useBuildReleases,
  useReleaseNoteLinks,
  seenAtLabel,
  versionLabel,
  FRONT_BUILD,
  type BuildRelease,
} from '@/lib/version';

/**
 * 버전 줄을 누르면 열리는 릴리즈 노트.
 *
 * <h2>보기가 둘이다 (사용자 지시 2026-09-30)</h2>
 * <b>날짜별</b>은 「무엇이 바뀌었나」 를 사람의 말로 읽는 순서고, <b>Job 버전별</b>은 「V2.0.812.430
 * 이 판에 무엇이 들어 있나」 를 묻는 순서다. 뒤쪽이 필요한 이유는 젠킨스 잡이 <b>따로</b> 돌기
 * 때문이다 — 사람은 사이드바에 적힌 그 쌍을 들고 문의한다.
 *
 * <p>번호는 손으로 적지 않는다. 떠 있는 판이 스스로 남긴 목록(V361 · {@link useBuildReleases})에,
 * 번들이 처음 뜰 때 알린 노트 키가 <b>그 판에 저장</b>된다(V448 · {@link useReleaseNoteLinks}).
 * 판 연결과 숨김은 환경설정 › 변경이력에서 고친다.
 *
 * <h2>맨 위 하나만 펴 둔다</h2>
 * 노트가 열여섯 덩이라 전부 펴면 창을 열자마자 스크롤바가 생기고, 사람이 찾는 <b>「이번에 뭐가
 * 바뀌었나」</b>가 옛 기록에 묻힌다. 여러 개를 동시에 펼 수 있게 둔 것은 지난주와 이번주를 나란히
 * 볼 자리가 필요하기 때문이다.
 */
export function ReleaseNotesModal({
  onClose,
  onNavigate = onClose,
}: {
  onClose: () => void;
  /**
   * 줄을 눌러 <b>다른 화면으로 갈 때</b>. 기본은 닫기와 같다.
   *
   * <p>따로 받는 것은 모바일 서랍 때문이다 — 서랍은 «경로가 바뀌면» 닫히는데, 같은 화면의 탭으로
   * 가는 줄(`/sites/pmis?tab=…`)은 경로가 그대로라 서랍이 열린 채 남는다. 부르는 쪽이 서랍까지
   * 닫을 수 있게 길을 낸다.
   */
  onNavigate?: () => void;
}) {
  const version = useAppVersion();
  const [view, setView] = useState<'date' | 'build'>('date');

  return (
    <Modal
      title="릴리즈 노트"
      onClose={onClose}
      maxWidth="760px"
      actions={
        <div style={{ display: 'flex', gap: '4px' }}>
          <ViewTab on={view === 'date'} onClick={() => setView('date')}>날짜별</ViewTab>
          <ViewTab on={view === 'build'} onClick={() => setView('build')}>Job 버전별</ViewTab>
        </div>
      }
    >
      <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)' }}>{version}</div>

      {view === 'date' ? <DateView onNavigate={onNavigate} /> : <BuildView onNavigate={onNavigate} />}
    </Modal>
  );
}

/** 제목 줄의 보기 스위치. 모달 버튼은 제목 줄이 이 앱의 규칙이라 탭도 그 자리에 선다. */
function ViewTab({ on, onClick, children }: { on: boolean; onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={on ? 'btn-primary' : 'btn-ghost'}
      style={{ fontSize: '12px', padding: '4px 10px' }}
    >
      {children}
    </button>
  );
}

/** 날짜별 — 사람이 적은 순서 그대로. */
function DateView({ onNavigate }: { onNavigate: () => void }) {
  // 같은 날짜 덩이가 둘일 수 있어(10-06) 날짜만으로는 열쇠가 겹친다 — 순번을 붙인다.
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set([foldKey(RELEASE_NOTES_BY_DATE[0], 0)]));
  const hidden = useHiddenKeys();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {RELEASE_NOTES_BY_DATE.map((entry, at) => (
        <Fold
          key={foldKey(entry, at)}
          tag={entry.period}
          title={entry.title}
          expanded={open.has(foldKey(entry, at))}
          onToggle={() => setOpen((was) => toggled(was, foldKey(entry, at)))}
        >
          <ItemList
            items={entry.items.filter((item) => !hidden.has(noteKey(entry.period, item.text)))}
            onNavigate={onNavigate}
          />
        </Fold>
      ))}
    </div>
  );
}

/**
 * Job 버전별 — 관측된 판마다, 그 판에 처음 실려 나간 노트.
 *
 * <h2>저장된 판을 읽는다 (V448 · 사용자 지적 2026-10-14)</h2>
 * 예전에는 노트 날짜 0시와 판이 처음 보인 시각을 견주어 <b>열 때마다 짐작</b>했다. 그래서 하루에 두
 * 판이 나가면 그날 노트가 전부 아침 판에 붙고 오후 판은 「노트 0건」 이 됐다. 이제는 서버가 번들의
 * 보고로 저장한 판을 그대로 쓴다. 옛 서버가 떠 있어 판 id·연결이 없을 때만 날짜 규칙으로 물러선다.
 *
 * <h2>V361 앞의 판은 목록에 없다</h2>
 * 표가 이제부터 쌓이므로 처음에는 줄이 없거나 하나다. 지어내지 않는다 — 옛 이력은 날짜별 보기가
 * 이미 갖고 있다.
 */
function BuildView({ onNavigate }: { onNavigate: () => void }) {
  const builds = useBuildReleases();
  const links = useReleaseNoteLinks();
  const backendBuild = useBackendBuild();
  /*
    <b>맨 위를 펴 둔다</b> (사용자 지적 2026-09-30). 전부 접어 두었더니 판 줄만 서고 내용이 없는
    화면이 되어, 「Job 버전별은 아예 안 나온다」 로 읽혔다. 날짜별과 같은 규칙이어야 한다.

    ★ 판 목록은 서버에서 오므로 <b>첫 그림에는 비어 있다</b>. useState 초기값으로는 첫 줄의 키를
      알 수 없어, 「아직 아무것도 안 눌렀다」(null)와 「다 접었다」(빈 집합)를 구분해 둔다.
  */
  const [open, setOpen] = useState<ReadonlySet<string> | null>(null);

  if (builds.isPending || links.isPending) return <LoadingBlock />;

  const rows = builds.data ?? [];
  const newest = rows[0];
  if (!newest) {
    return <div style={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>아직 관측된 판이 없습니다.</div>;
  }

  const firstKey = keyOf(newest);
  const opened = open ?? new Set([firstKey]);
  const stored =
    links.data && newest.id
      ? groupNotesByBuild(RELEASE_NOTE_ROWS, links.data, new Set(rows.flatMap((row) => (row.id ? [row.id] : []))))
      : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {rows.map((row, at) => {
        const key = keyOf(row);
        const running = row.backendBuild === backendBuild && row.frontBuild === FRONT_BUILD;
        const items = stored ? (stored.byBuild.get(row.id ?? '') ?? []) : notesIn(rows[at + 1], row);
        return (
          <Fold
            key={key}
            tag={versionLabel(row.backendBuild, row.frontBuild)}
            // 펴 보기 전에 <b>얼마나 들었는지</b>를 함께 적는다 — 빈 판을 눌러 보고 비어 있음을
            // 확인하게 두면, 그 자리가 고장으로 읽힌다.
            title={`${seenAtLabel(row.firstSeenAt)} · 노트 ${items.length}건`}
            badge={running ? '지금 이 판' : undefined}
            expanded={opened.has(key)}
            onToggle={() => setOpen((was) => toggled(was ?? new Set([firstKey]), key))}
          >
            {items.length > 0 ? (
              <ItemList items={items} onNavigate={onNavigate} />
            ) : (
              <div style={{ padding: '0 12px 10px 12px', fontSize: '12.5px', color: 'var(--text-muted)' }}>
                이 판에서 새로 적힌 노트가 없습니다.
              </div>
            )}
          </Fold>
        );
      })}
      {/*
        아직 어느 판에도 저장되지 않은 줄(이 번들의 보고 전 · 관리자가 판을 비움). 감추면 그 줄이 Job
        보기에서 통째로 사라진다 — 숨긴 것 말고는 하나도 빠지지 않아야 한다(사용자 지시 2026-10-14).
      */}
      {stored && stored.unassigned.length > 0 && (
        <Fold
          tag="판 미지정"
          title={`노트 ${stored.unassigned.length}건`}
          expanded={opened.has(UNASSIGNED_KEY)}
          onToggle={() => setOpen((was) => toggled(was ?? new Set([firstKey]), UNASSIGNED_KEY))}
        >
          <ItemList items={stored.unassigned} onNavigate={onNavigate} />
        </Fold>
      )}
    </div>
  );
}

const UNASSIGNED_KEY = '__unassigned__';

/** 날짜별 덩이의 열쇠 — 날짜가 같은 덩이가 있어 순번을 붙인다. */
function foldKey(entry: { period: string } | undefined, at: number): string {
  return `${entry?.period ?? ''}#${at}`;
}

/** 판 하나를 가리키는 열쇠 — 표의 유일 키(백엔드, 프런트)와 같은 쌍이다. */
function keyOf(row: BuildRelease): string {
  return `${row.backendBuild}.${row.frontBuild}`;
}

/** 감춘 노트 키. 못 읽었으면 비어 있다 — 노트를 감추는 쪽으로 틀리지 않는다. */
function useHiddenKeys(): ReadonlySet<string> {
  const links = useReleaseNoteLinks();
  return new Set((links.data ?? []).filter((link) => link.hidden).map((link) => link.noteKey));
}

/**
 * 옛 서버용 물러섬 — 두 판 사이에 나간 노트. {@code after} 가 없으면(가장 오래된 판) 아래 경계가 없다.
 *
 * <p>날짜는 그 날 0시로 읽는다. 노트를 적고 그날 배포하는 것이 보통이라, 같은 날의 노트는
 * <b>그날 처음 나간 판</b>에 들어가야 맞다.
 */
function notesIn(after: BuildRelease | undefined, until: BuildRelease): ReleaseItem[] {
  const lower = after ? Date.parse(after.firstSeenAt) : Number.NEGATIVE_INFINITY;
  const upper = Date.parse(until.firstSeenAt);

  return RELEASE_NOTE_ROWS.filter((row) => {
    const at = Date.parse(`${row.date}T00:00:00+09:00`);
    return Number.isFinite(at) && at > lower && at <= upper;
  });
}

function toggled(was: ReadonlySet<string>, key: string): ReadonlySet<string> {
  const next = new Set(was);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

/** 접히는 덩이 한 칸. 날짜별·Job 별이 같은 모양이라 두 보기를 오갈 때 눈이 다시 적응하지 않는다. */
function Fold({
  tag,
  title,
  badge,
  expanded,
  onToggle,
  children,
}: {
  tag: string;
  title: string;
  badge?: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: '10px' }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="hover:bg-slate-50"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          width: '100%',
          padding: '10px 12px',
          background: 'transparent',
          border: 'none',
          borderRadius: '10px',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          style={{
            flexShrink: 0,
            color: 'var(--text-muted)',
            transform: expanded ? 'rotate(90deg)' : 'none',
            transition: 'transform 120ms',
          }}
        >
          <path d="M9 6l6 6-6 6" />
        </svg>
        <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', flexShrink: 0 }}>{tag}</span>
        <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', minWidth: 0 }}>{title}</span>
        {badge && (
          <span
            style={{
              flexShrink: 0,
              marginLeft: 'auto',
              fontSize: '10.5px',
              fontWeight: 700,
              color: '#1d4ed8',
              background: '#eff6ff',
              borderRadius: '6px',
              padding: '2px 6px',
            }}
          >
            {badge}
          </span>
        )}
      </button>

      {expanded && children}
    </div>
  );
}

function ItemList({ items, onNavigate }: { items: readonly ReleaseItem[]; onNavigate: () => void }) {
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: '0 12px 10px 12px' }}>
      {items.map((item) => (
        <li
          key={item.text}
          style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'flex-start',
            padding: '5px 0',
            borderTop: '1px solid var(--border)',
          }}
        >
          <AreaChip area={item.area} />
          <ItemText item={item} onNavigate={onNavigate} />
        </li>
      ))}
    </ul>
  );
}

/**
 * 노트 한 줄의 문장. {@code href} 가 있으면 <b>그 화면으로 가는 링크</b>가 되고, 가면서 노트를 닫는다
 * (사용자 지시 2026-09-30).
 *
 * <h2>닫고 간다</h2>
 * 노트를 띄운 채로 화면만 바뀌면, 도착한 곳이 어두운 덮개 뒤에 가려 <b>무엇을 눌렀는지</b>를 볼 수
 * 없다. 링크를 눌렀다는 것은 「가서 보겠다」 는 뜻이라 창을 남겨 둘 이유가 없다.
 *
 * <h2>화살표는 링크인 줄에만</h2>
 * 갈 곳이 없는 줄(스키마 정리·장애 보수)도 같은 모양이면, 눌러 보고 아무 일도 안 일어나는 경험을
 * 준다. 오른쪽 화살표가 <b>누를 수 있는 줄</b>의 표시다 — 밑줄은 마우스를 올렸을 때만 선다.
 */
function ItemText({ item, onNavigate }: { item: ReleaseItem; onNavigate: () => void }) {
  const textStyle = { fontSize: '12.5px', lineHeight: 1.6, color: 'var(--text-secondary)' } as const;

  if (!item.href) return <span style={textStyle}>{item.text}</span>;

  return (
    <Link
      to={item.href}
      onClick={onNavigate}
      className="hover:underline"
      style={{ ...textStyle, display: 'flex', gap: '6px', alignItems: 'baseline', minWidth: 0 }}
    >
      <span style={{ minWidth: 0 }}>{item.text}</span>
      <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        style={{ flexShrink: 0, color: 'var(--text-muted)', transform: 'translateY(1px)' }}
        aria-hidden="true"
      >
        <path d="M5 12h14M13 6l6 6-6 6" />
      </svg>
    </Link>
  );
}

/**
 * 줄 앞의 갈래표. <b>색을 갈래마다 주지 않는다</b> — 열여섯 갈래에 색을 매기면 노트가 색표처럼
 * 읽히고, 정작 읽어야 할 문장이 뒤로 물러난다. 폭을 고정해 문장의 왼쪽 선이 맞는다.
 */
export function AreaChip({ area }: { area: ReleaseArea }) {
  return (
    <span
      style={{
        flexShrink: 0,
        width: '44px',
        textAlign: 'center',
        fontSize: '10.5px',
        fontWeight: 700,
        color: 'var(--text-muted)',
        background: 'var(--surface-muted, #f1f5f9)',
        borderRadius: '6px',
        padding: '2px 0',
        marginTop: '2px',
      }}
    >
      {area}
    </span>
  );
}
